import type { TFunction } from 'i18next'

import {
  addDays,
  addMonths,
  isoDay,
  lastMonth,
  startOfMonth,
  startOfYear,
  type DateRange,
  type DateRangePreset,
} from '@/lib/dates'

/**
 * The date chips of the two AMS pages' pickers, built as of `today` each
 * render so they stay right across midnight. Every range ends today except
 * "Last month".
 */

function lastDays(id: string, label: string, days: number, today: string): DateRangePreset {
  return { id, label, range: { from: addDays(today, -(days - 1)), to: today } }
}

/**
 * AMS View's Date range chips (VentaHub `pages/ams_view/AmsViewPage.tsx:1820-1832`):
 * Last 7 / 14 / 30 / 60 / 90 days, Month to date, Last month, Year to date.
 */
export function amsDatePresets(today: Date, t: TFunction): DateRangePreset[] {
  const day = isoDay(today)
  return [
    lastDays('last7', t('amsShared.datePresets.last7'), 7, day),
    lastDays('last14', t('amsShared.datePresets.last14'), 14, day),
    lastDays('last30', t('amsShared.datePresets.last30'), 30, day),
    lastDays('last60', t('amsShared.datePresets.last60'), 60, day),
    lastDays('last90', t('amsShared.datePresets.last90'), 90, day),
    {
      id: 'monthToDate',
      label: t('amsShared.datePresets.monthToDate'),
      range: { from: startOfMonth(day), to: day },
    },
    { id: 'lastMonth', label: t('amsShared.datePresets.lastMonth'), range: lastMonth(today) },
    {
      id: 'yearToDate',
      label: t('amsShared.datePresets.yearToDate'),
      range: { from: startOfYear(day), to: day },
    },
  ]
}

/**
 * Review AMS Data's Current period chips (VentaHub
 * `pages/review_ams_data/ReviewAmsDataPage.tsx:212-225`): Last 7 / 14 / 30
 * days, This month, Last month. The Previous period picker has none.
 */
export function reviewDatePresets(today: Date, t: TFunction): DateRangePreset[] {
  const day = isoDay(today)
  return [
    lastDays('last7', t('amsShared.datePresets.last7'), 7, day),
    lastDays('last14', t('amsShared.datePresets.last14'), 14, day),
    lastDays('last30', t('amsShared.datePresets.last30'), 30, day),
    {
      id: 'thisMonth',
      label: t('amsShared.datePresets.thisMonth'),
      range: { from: startOfMonth(day), to: day },
    },
    { id: 'lastMonth', label: t('amsShared.datePresets.lastMonth'), range: lastMonth(today) },
  ]
}

/**
 * AMS View's range before anyone picks one (`AmsViewPage.tsx:384-393`): this
 * month so far after the 5th; in the first five days, from the start of last
 * month, so the page never opens on a nearly empty window.
 */
export function amsDefaultRange(today: Date): Required<DateRange> {
  const day = isoDay(today)
  const from = today.getDate() > 5 ? startOfMonth(day) : addMonths(day, -1)
  return { from, to: day }
}
